// src/App.jsx
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ChakraProvider, extendTheme, Flex, Spinner } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { supabase } from './config/supabaseClient';
import ReactGA from "react-ga4";
import ScrollToTop from './components/ScrollToTop';
import AlbumProvider from './contexts/AlbumContext'; // Importe o provedor de contexto

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Verification from './pages/Verification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthRedirect from './pages/AuthRedirect';
import CompleteProfile from './pages/CompleteProfile';
import AlbumCreator from './pages/AlbumCreator'; // Importe a nova página

// Footer
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Contact from './pages/Contact';

// Inicialização do Google Analytics
// Substitua "G-XXXXXXXXXX" pelo seu ID real do GA4
ReactGA.initialize("G-4N27DSR6MX");

// Definindo o tema personalizado
const theme = extendTheme({
  colors: {
    brand: {
      primary: '#A5B4E8',
      secondary: '#B8E8C7',
      accent1: '#FFECB3',
      accent2: '#F5C3D8',
    },
  },
  fonts: {
    heading: 'Quicksand, sans-serif',
    body: 'Quicksand, sans-serif',
  },
});

// Componente para rastrear visualizações de página
const PageTracker = () => {
  const location = window.location;

  useEffect(() => {
    // Envia um pageview quando o componente é montado
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search
    });
  }, [location.pathname, location.search]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar se o usuário está logado
    const getUser = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        // Verificação explícita da sessão e do usuário
        if (data && data.session && data.session.user) {
          setUser(data.session.user);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error("Erro ao verificar sessão:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    getUser();

    // Ouvir mudanças de autenticação
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <Flex height="100vh" align="center" justify="center">
        <Spinner size="xl" color="brand.primary" thickness="4px" />
      </Flex>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Componente para proteger a página CompleteProfile e verificar se o perfil está completo
const CompleteProfileRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      try {
        // Verificar sessão
        const { data, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (!data || !data.session) {
          setUser(null);
          setLoading(false);
          return;
        }

        setUser(data.session.user);

        // Verificar se o perfil está completo
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('cpf_cnpj')
          .eq('id', data.session.user.id)
          .single();
          
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Erro ao verificar perfil:', profileError);
        }
        
        // Se o perfil estiver completo, marcar como completo
        if (profileData?.cpf_cnpj) {
          setProfileComplete(true);
        } else {
          setProfileComplete(false);
        }
        
      } catch (error) {
        console.error('Erro ao verificar usuário:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, [navigate]);

  if (loading) {
    return (
      <Flex height="100vh" align="center" justify="center">
        <Spinner size="xl" color="brand.primary" thickness="4px" />
      </Flex>
    );
  }

  // Se não estiver logado, redirecionar para login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Se o perfil estiver completo, redirecionar para dashboard
  if (profileComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  // Caso contrário, mostrar o componente CompleteProfile
  return children;
};

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AlbumProvider> {/* Envolver toda a aplicação com o provedor de contexto do álbum */}
        <Router>
          {/* Componente que rola para o topo em cada mudança de rota */}
          <ScrollToTop />
          <PageTracker />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verification" element={<Verification />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth-redirect" element={<AuthRedirect />} />
            
            {/* Rota para completar perfil (protegida) */}
            <Route
              path="/complete-profile"
              element={
                <CompleteProfileRoute>
                  <CompleteProfile />
                </CompleteProfileRoute>
              }
            />

            <Route path="/sobre-nos" element={<AboutUs />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
            <Route path="/termos-de-uso" element={<TermsOfUse />} />
            <Route path="/contato" element={<Contact />} />
            
            {/* Rotas protegidas */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Nova rota para o criador de álbum */}
            <Route
              path="/album-creator"
              element={
                <ProtectedRoute>
                  <AlbumCreator />
                </ProtectedRoute>
              }
            />

            {/* Rota para qualquer outro caminho */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AlbumProvider>
    </ChakraProvider>
  );
}

export default App;